import { ERROR_401 } from 'common/config/api';
import { SIGN_IN_REASON, SIGN_IN_VIEW } from 'common/constants/auth';
import {
  buildSignInUrl,
  singleFlightTokenRefresh,
} from 'pages/Auth/utils';
import BaseService from './base';

type AuthErrorLike = {
  // Only the top-level `.status` shape is needed here — axios-shaped errors are not used in this service layer.
  status?: number;
};

const isAuthError = (error: unknown): error is AuthErrorLike =>
  typeof error === 'object' && error !== null && 'status' in error;

// HMAC portal 401s mean the request link is invalid or expired, not that a JWT
// session died. Treating them as auth expiry sends anonymous company users to login.
const isHmacPortalRequest = (url: string): boolean =>
  /\/api\/(?:documents|questionnaires)\/requests\/(?:validate-token\/|file-upload-status\/|\d+\/)/.test(
    url
  );

const handleExpiredSession = () => {
  // A full document navigation remounts the app, which used to destroy the snackbar this once showed —
  // the user landed on a clean sign-in page with no explanation. The reason travels in the URL instead,
  // so the redirect can also happen immediately rather than after a delay spent firing doomed requests.
  window.location.href = buildSignInUrl({
    reason: SIGN_IN_REASON.SESSION_EXPIRED,
    returnTo: `${window.location.pathname}${window.location.search}`,
    view: SIGN_IN_VIEW.IDENTIFIER,
  });
};

const refreshPassThrough = async <TResponse>(
  url: string,
  next: () => Promise<TResponse>
): Promise<TResponse> => {
  try {
    return await next();
  } catch (error) {
    if (isAuthError(error) && error.status === Number(ERROR_401) && !isHmacPortalRequest(url)) {
      const result = await singleFlightTokenRefresh();

      if (result.kind === 'success') {
        return next();
      }

      if (result.kind === 'cleared') {
        handleExpiredSession();
      }
      // 'transient': rethrow the original 401 without triggering logout
    }
    throw error;
  }
};

class BaseServiceRetryWrapper extends BaseService {
  async get<TResponse = unknown>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(url, () => super.get<TResponse>(url, headers));
  }

  async getFile<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(url, () => super.getFile<TResponse, TPayload>(url, data, headers));
  }

  async downloadFile<TResponse = Blob>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(url, () => super.downloadFile<TResponse>(url, headers));
  }

  async put<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(url, () => super.put<TResponse, TPayload>(url, data, headers));
  }

  async post<TResponse = unknown, TPayload = unknown>(
    url: string,
    data?: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(url, () => super.post<TResponse, TPayload>(url, data, headers));
  }

  async delete<TResponse = unknown>(url: string, headers?: Record<string, string>): Promise<TResponse> {
    return refreshPassThrough(url, () => super.delete<TResponse>(url, headers));
  }

  async patch<TResponse = unknown, TPayload = unknown>(
    url: string,
    data: TPayload,
    headers?: Record<string, string>
  ): Promise<TResponse> {
    return refreshPassThrough(url, () => super.patch<TResponse, TPayload>(url, data, headers));
  }
}

export default BaseServiceRetryWrapper;
